import React, { useState, useEffect } from 'react';
import { ChevronLeft, Download, FileText, Check, Eye, X, Loader2, Star, TrendingUp, Award, ShieldCheck, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { BookMockup, getBadge } from '../components/BookMockup';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { createCheckoutSession, getRelatedProducts, getPricingTiers } from '../services/paymentService';
import { HorizontalScroll } from '../components/HorizontalScroll';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GuideDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [related, setRelated] = useState([]);
  const [tiers, setTiers] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [prodResp, relResp, tierResp] = await Promise.all([
          axios.get(`${API}/products/${id}`),
          getRelatedProducts(id).catch(() => []),
          getPricingTiers(id).catch(() => ({})),
        ]);
        setProduct(prodResp.data);
        setRelated(relResp);
        setTiers(tierResp);
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handlePurchase = async (productId) => {
    try {
      setPurchasing(true);
      const session = await createCheckoutSession(productId || product.id);
      if (session.url) window.location.href = session.url;
    } catch (error) {
      console.error('Purchase error:', error);
      alert('Unable to process purchase. Please try again.');
    } finally {
      setPurchasing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="guide-loading">
        <Loader2 className="w-12 h-12 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (!product || product.error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Guide not found</h2>
          <Button onClick={() => window.location.href = '/browse'} data-testid="back-to-shop-btn">Back to Shop</Button>
        </div>
      </div>
    );
  }

  const crossSellItems = tiers.cross_sell || related;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navbar />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* 1. Product Title + Short Benefit Summary */}
        <div className="text-center mb-8" data-testid="product-header">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3" data-testid="product-title">
            {product.title}
          </h1>
          <p className="text-gray-600 text-base md:text-lg max-w-2xl mx-auto" data-testid="product-description">
            {product.standard_description
              ? product.standard_description.split('\n')[0]
              : product.description}
          </p>
          {product.download_count > 0 && (
            <div className="mt-3 text-gray-400 text-sm flex items-center justify-center gap-1">
              <Download size={14} /> {product.download_count >= 1000 ? `${(product.download_count / 1000).toFixed(1)}k` : product.download_count} downloads
            </div>
          )}
        </div>

        {/* 2. What's Included — Book Mockup + Checklist + Download/Preview */}
        <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden mb-8 relative" data-testid="product-detail-card">
          {/* Badge on the card, not the book */}
          {getBadge(product) && (() => {
            const badgeKey = getBadge(product);
            const badgeMap = { trending: { bg: 'bg-red-500', emoji: '🔥', label: 'Trending' }, fast: { bg: 'bg-amber-500', emoji: '⚡', label: 'Fast Results' }, downloaded: { bg: 'bg-blue-500', emoji: '⭐', label: 'Most Downloaded' }, beginner: { bg: 'bg-orange-500', emoji: '🧠', label: 'Beginner Friendly' }, value: { bg: 'bg-green-500', emoji: '💰', label: 'Best Value' } };
            const b = badgeMap[badgeKey];
            return b ? <div className={`absolute top-3 left-3 z-20 ${b.bg} text-white text-xs font-bold px-2.5 py-1 rounded-lg shadow-lg flex items-center gap-1`}><span>{b.emoji}</span><span>{b.label}</span></div> : null;
          })()}
          <div className="grid md:grid-cols-2 gap-0">
            <div className="relative min-h-[320px] flex items-center justify-center bg-white">
              <BookMockup productId={product.id} title={product.title} size="xl" />
            </div>

            <div className="p-6 md:p-8">
              <h3 className="text-lg font-bold text-gray-900 mb-3">What's Included in This Guide:</h3>
              <ul className="space-y-2 mb-6">
                {(product.bullets || [
                  'Step-by-step legal templates ready to use',
                  'Covers real-life situations with proven formats',
                  'Includes editable documents and instructions',
                  'Designed for fast action and results',
                  'Suitable for personal or business use',
                ]).map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600 shrink-0" />
                    <span className="text-gray-700 text-sm">{item}</span>
                  </li>
                ))}
              </ul>

              <div className="space-y-3">
                <Button
                  className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white text-lg py-6 hover:from-orange-600 hover:to-red-700 shadow-lg"
                  onClick={() => handlePurchase(product.id)}
                  disabled={purchasing}
                  data-testid="buy-now-btn"
                >
                  {purchasing ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processing...</>
                  ) : (
                    <>Download Now — ${product.price}</>
                  )}
                </Button>

                <Button
                  variant="outline"
                  className="w-full border-2 border-blue-500 text-blue-700 hover:bg-blue-50 text-base py-5"
                  onClick={() => setShowPreview(true)}
                  data-testid="preview-btn"
                >
                  <Eye size={18} className="mr-2" />
                  Preview Guide (First 2 Pages)
                </Button>
              </div>

              <p className="text-center text-xs text-gray-400 mt-2">
                Prefer reading on Kindle?{' '}
                <a href="https://www.amazon.com" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-orange-600 underline transition-colors" data-testid="kindle-link">
                  View this guide on Amazon Kindle
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* 3. Trust Section — "Why These Templates Work" */}
        <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-6 md:p-8 mb-8" data-testid="trust-section">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-green-600" /> Why These Templates Work
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              'Built using structured formats similar to real legal and financial documents',
              'Designed for real-world situations, not generic examples',
              'Step-by-step layout so users know exactly what to do',
              'Clear and organized — no confusion',
              'Created to help users take action immediately',
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
                <span className="text-gray-700 text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* 4. Comparison Table — "Choose the Right Option for You" */}
        <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-6 md:p-8 mb-8" data-testid="comparison-table">
          <h2 className="text-xl font-bold text-gray-900 mb-5 text-center">Choose the Right Option for You</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 px-3 text-gray-600 font-semibold">Feature</th>
                  <th className="text-center py-3 px-3 text-gray-900 font-semibold">Single Guide</th>
                  <th className="text-center py-3 px-3 text-blue-700 font-semibold bg-blue-50/50">Bundle (5)</th>
                  <th className="text-center py-3 px-3 text-purple-700 font-semibold">Advanced ($89)</th>
                  <th className="text-center py-3 px-3 text-orange-700 font-semibold bg-orange-50/50">Full System ($150)</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-100">
                  <td className="py-3 px-3 text-gray-700 font-medium">Number of Templates</td>
                  <td className="py-3 px-3 text-center text-gray-600">1</td>
                  <td className="py-3 px-3 text-center text-gray-600 bg-blue-50/30">5</td>
                  <td className="py-3 px-3 text-center text-gray-600">10–15</td>
                  <td className="py-3 px-3 text-center font-semibold text-orange-700 bg-orange-50/30">Complete Access</td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-3 px-3 text-gray-700 font-medium">Instructions Included</td>
                  <td className="py-3 px-3 text-center text-gray-600">Detailed</td>
                  <td className="py-3 px-3 text-center text-gray-600 bg-blue-50/30">Detailed</td>
                  <td className="py-3 px-3 text-center text-gray-600">Advanced Step-by-Step</td>
                  <td className="py-3 px-3 text-center font-semibold text-orange-700 bg-orange-50/30">Full System Guidance</td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-3 px-3 text-gray-700 font-medium">Coverage</td>
                  <td className="py-3 px-3 text-center text-gray-600">One Issue</td>
                  <td className="py-3 px-3 text-center text-gray-600 bg-blue-50/30">Multiple Issues</td>
                  <td className="py-3 px-3 text-center text-gray-600">Expanded Coverage</td>
                  <td className="py-3 px-3 text-center font-semibold text-orange-700 bg-orange-50/30">Complete Solution</td>
                </tr>
                <tr>
                  <td className="py-3 px-3 text-gray-700 font-medium">Best For</td>
                  <td className="py-3 px-3 text-center text-gray-600">Quick Fix</td>
                  <td className="py-3 px-3 text-center text-gray-600 bg-blue-50/30">Most Users</td>
                  <td className="py-3 px-3 text-center text-gray-600">Serious Users</td>
                  <td className="py-3 px-3 text-center font-semibold text-orange-700 bg-orange-50/30">Full Control &amp; Maximum Results</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* 5. Pricing Section — ALL 4 OPTIONS */}
        <div className="mb-8" data-testid="pricing-section">
          <h2 className="text-xl font-bold text-gray-900 mb-5 text-center">Choose Your Option</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border-2 border-gray-200 p-5 flex flex-col relative" data-testid="tier-single">
              <div className="text-sm font-semibold text-gray-500 mb-1">Single Guide</div>
              <div className="text-3xl font-bold text-gray-900 mb-2">${product.price}</div>
              <div className="text-xs text-gray-500 mb-4">1 template</div>
              <ul className="space-y-1.5 mb-5 flex-1">
                <li className="flex items-center gap-1.5 text-xs text-gray-600"><Check size={12} className="text-green-500" />1 professional template</li>
                <li className="flex items-center gap-1.5 text-xs text-gray-600"><Check size={12} className="text-green-500" />Detailed instructions</li>
                <li className="flex items-center gap-1.5 text-xs text-gray-600"><Check size={12} className="text-green-500" />Instant PDF download</li>
              </ul>
              <Button className="w-full bg-gray-900 text-white hover:bg-gray-800 text-sm py-3" onClick={() => handlePurchase(product.id)} disabled={purchasing} data-testid="buy-single-btn">
                Get Instant Access
              </Button>
            </div>

            {tiers.bundle_5 && (
              <div className="bg-white rounded-xl border-2 border-blue-400 p-5 flex flex-col relative" data-testid="tier-bundle-5">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-[10px] font-bold px-3 py-1 rounded-full">Best Value</div>
                <div className="text-sm font-semibold text-blue-600 mb-1">Bundle (5 Guides)</div>
                <div className="text-3xl font-bold text-gray-900 mb-1">${tiers.bundle_5.price}</div>
                <div className="text-xs text-green-600 font-semibold mb-3">Save $20</div>
                <ul className="space-y-1.5 mb-5 flex-1">
                  <li className="flex items-center gap-1.5 text-xs text-gray-600"><Check size={12} className="text-green-500" />5 professional templates</li>
                  <li className="flex items-center gap-1.5 text-xs text-gray-600"><Check size={12} className="text-green-500" />Detailed instructions</li>
                  <li className="flex items-center gap-1.5 text-xs text-gray-600"><Check size={12} className="text-green-500" />Multiple issue coverage</li>
                </ul>
                <Button className="w-full bg-blue-600 text-white hover:bg-blue-700 text-sm py-3" onClick={() => window.location.href = `/bundle/${tiers.bundle_5.id}`} data-testid="buy-bundle5-btn">
                  Get Instant Access
                </Button>
              </div>
            )}

            {tiers.bundle_10 && (
              <div className="bg-white rounded-xl border-2 border-purple-400 p-5 flex flex-col relative" data-testid="tier-bundle-10">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-600 text-white text-[10px] font-bold px-3 py-1 rounded-full">Trending</div>
                <div className="text-sm font-semibold text-purple-600 mb-1">Advanced Bundle (10 Guides)</div>
                <div className="text-3xl font-bold text-gray-900 mb-1">${tiers.bundle_10.price}</div>
                <div className="text-xs text-green-600 font-semibold mb-3">Save $30</div>
                <ul className="space-y-1.5 mb-5 flex-1">
                  <li className="flex items-center gap-1.5 text-xs text-gray-600"><Check size={12} className="text-green-500" />10-15 professional templates</li>
                  <li className="flex items-center gap-1.5 text-xs text-gray-600"><Check size={12} className="text-green-500" />Advanced step-by-step</li>
                  <li className="flex items-center gap-1.5 text-xs text-gray-600"><Check size={12} className="text-green-500" />Expanded coverage</li>
                </ul>
                <Button className="w-full bg-purple-600 text-white hover:bg-purple-700 text-sm py-3" onClick={() => window.location.href = `/bundle/${tiers.bundle_10.id}`} data-testid="buy-bundle10-btn">
                  Get Instant Access
                </Button>
              </div>
            )}

            {tiers.full_system && (
              <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl border-2 border-orange-500 p-5 flex flex-col relative shadow-lg ring-2 ring-orange-200" data-testid="tier-full-system">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange-600 text-white text-[10px] font-bold px-3 py-1 rounded-full">Popular</div>
                <div className="text-sm font-semibold text-orange-600 mb-1">Full System</div>
                <div className="text-3xl font-bold text-gray-900 mb-1">${tiers.full_system.price}</div>
                <div className="text-xs text-green-600 font-semibold mb-3">Save $89</div>
                <ul className="space-y-1.5 mb-5 flex-1">
                  <li className="flex items-center gap-1.5 text-xs text-gray-600"><Check size={12} className="text-green-500" />Complete access</li>
                  <li className="flex items-center gap-1.5 text-xs text-gray-600"><Check size={12} className="text-green-500" />Full system guidance</li>
                  <li className="flex items-center gap-1.5 text-xs text-gray-600"><Check size={12} className="text-green-500" />Maximum results</li>
                </ul>
                <Button className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white hover:from-orange-600 hover:to-red-700 text-sm py-3 shadow-md" onClick={() => window.location.href = `/bundle/${tiers.full_system.id}`} data-testid="buy-system-btn">
                  Get Instant Access
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* 6. Downsell */}
        <div className="bg-gray-50 rounded-xl border border-gray-200 p-5 mb-8 text-center" data-testid="downsell-section">
          <p className="text-gray-600 text-sm mb-3">Not ready for the full bundle?</p>
          <Button variant="outline" className="border-gray-300 text-gray-700 hover:bg-white text-sm" onClick={() => handlePurchase(product.id)} disabled={purchasing} data-testid="downsell-single-btn">
            Start with a Single Guide — ${product.price}
          </Button>
        </div>

        {/* 7. Cross-sell: People also bought — Horizontal Swipe */}
        {crossSellItems.length > 0 && (
          <div className="mb-8" data-testid="cross-sell-section">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">People also bought</h2>
            <HorizontalScroll>
              {crossSellItems.slice(0, 8).map((item) => (
                <a
                  key={item.id}
                  href={`/guide/${item.id}`}
                  className="snap-start shrink-0 w-[200px] bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 border border-gray-200 hover:border-orange-300 block"
                  data-testid={`cross-sell-${item.id}`}
                >
                  <BookMockup productId={item.id} title={item.title} size="card" badge={getBadge(item)} />
                  <div className="p-3 flex items-center justify-between">
                    <h3 className="text-xs font-bold text-gray-900 line-clamp-2">{item.title}</h3>
                    <span className="text-red-700 font-bold text-xs ml-2 whitespace-nowrap">${item.price}</span>
                  </div>
                </a>
              ))}
            </HorizontalScroll>
          </div>
        )}

        {/* 8. Post-Purchase Upsell */}
        {(tiers.bundle_5 || tiers.full_system) && (
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 md:p-8 text-white text-center" data-testid="post-purchase-upsell">
            <h2 className="text-xl font-bold mb-2">Upgrade and get everything</h2>
            <p className="text-gray-400 text-sm mb-5">Get more templates and save more</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {tiers.bundle_5 && (
                <Button className="bg-blue-600 text-white hover:bg-blue-700 text-sm px-6" onClick={() => window.location.href = `/bundle/${tiers.bundle_5.id}`} data-testid="upsell-bundle-btn">
                  Upgrade to Bundle — ${tiers.bundle_5.price} <ArrowRight size={14} className="ml-1" />
                </Button>
              )}
              {tiers.bundle_10 && (
                <Button className="bg-purple-600 text-white hover:bg-purple-700 text-sm px-6" onClick={() => window.location.href = `/bundle/${tiers.bundle_10.id}`} data-testid="upsell-advanced-btn">
                  Advanced Bundle — ${tiers.bundle_10.price} <ArrowRight size={14} className="ml-1" />
                </Button>
              )}
              {tiers.full_system && (
                <Button className="bg-gradient-to-r from-orange-500 to-red-600 text-white hover:from-orange-600 hover:to-red-700 text-sm px-6" onClick={() => window.location.href = `/bundle/${tiers.full_system.id}`} data-testid="upsell-system-btn">
                  Upgrade to Full System — ${tiers.full_system.price} <ArrowRight size={14} className="ml-1" />
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Preview Modal — TOC + Template side by side, 60% blurred */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4" data-testid="preview-modal">
          <div className="bg-white rounded-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 md:p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900">Guide Preview</h3>
                <p className="text-sm text-gray-600">{product.title}</p>
              </div>
              <button onClick={() => setShowPreview(false)} className="text-gray-500 hover:text-gray-700 p-1" data-testid="close-preview-btn">
                <X size={24} />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4 md:p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="preview-pages">
                {/* Table of Contents Page */}
                <div data-testid="preview-toc-page">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Table of Contents</p>
                  <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm relative">
                    <img
                      src={`${API}/payment/preview-image/${product.id}/toc`}
                      alt="Table of Contents"
                      className="w-full h-auto"
                      style={{ filter: 'blur(3px)' }}
                    />
                    <div className="absolute inset-0 bg-white/30 flex items-center justify-center">
                      <div className="bg-white/90 backdrop-blur-sm rounded-lg px-4 py-3 text-center shadow-md">
                        <FileText className="w-6 h-6 text-orange-500 mx-auto mb-1" />
                        <p className="text-xs font-semibold text-gray-700">Full Table of Contents</p>
                        <p className="text-[10px] text-gray-500">Purchase to view</p>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Template Page */}
                <div data-testid="preview-template-page">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Template Page</p>
                  <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm relative">
                    <img
                      src={`${API}/payment/preview-image/${product.id}/template`}
                      alt="Template page"
                      className="w-full h-auto"
                      style={{ filter: 'blur(3px)' }}
                    />
                    <div className="absolute inset-0 bg-white/30 flex items-center justify-center">
                      <div className="bg-white/90 backdrop-blur-sm rounded-lg px-4 py-3 text-center shadow-md">
                        <FileText className="w-6 h-6 text-orange-500 mx-auto mb-1" />
                        <p className="text-xs font-semibold text-gray-700">Ready-to-Use Template</p>
                        <p className="text-[10px] text-gray-500">Purchase to unlock</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-4 md:p-6 border-t border-gray-200 bg-gray-50 flex flex-col sm:flex-row gap-3">
              <Button
                className="flex-1 bg-gradient-to-r from-orange-500 to-red-600 text-white"
                onClick={() => { setShowPreview(false); handlePurchase(product.id); }}
                disabled={purchasing}
                data-testid="preview-purchase-btn"
              >
                {purchasing ? 'Processing...' : `Unlock Full Guide — $${product.price}`}
              </Button>
              <Button variant="outline" onClick={() => setShowPreview(false)} data-testid="preview-close-btn">
                Close Preview
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Sticky "Add to Bundle" Bar */}
      {product.product_type === 'guide' && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-gradient-to-r from-indigo-600 via-blue-600 to-indigo-600 shadow-[0_-4px_20px_rgba(0,0,0,0.15)] border-t border-blue-500/30" data-testid="sticky-bundle-bar">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <div className="text-white min-w-0">
              <p className="text-xs sm:text-sm font-bold leading-tight">Add 4 more guides for only $39</p>
              <p className="text-[10px] sm:text-xs text-blue-200 leading-tight">Mix & match from any category — save up to 70%</p>
            </div>
            <a
              href="/build-bundle"
              className="shrink-0 bg-white text-blue-700 hover:bg-blue-50 font-bold text-xs sm:text-sm px-4 sm:px-6 py-2.5 rounded-lg shadow-md transition-colors"
              data-testid="sticky-bundle-btn"
            >
              Build Bundle
            </a>
          </div>
        </div>
      )}
      <Footer />
    </div>

  );
};

export default GuideDetail;